import { CustomTimezones, LibrarySymbolInfo, ResolutionString } from '@/charting_library';
import { DEFAULT_CHAIN_ID } from '@/constants/chain';
import { QUERY_KEYS } from '@/constants/query';
import { IMarketPair } from '@/features/market/types';

import { queryClient } from '@/global';
import { KlineInterval } from '@/types/chart';
import BigNumber from 'bignumber.js';
import { isMobile } from 'react-device-detect';
import { TVResolution, TvTheme, colors, defaultResolutionString } from './constants';

// tslint:disable-next-line:no-any
/* eslint-disable @typescript-eslint/no-explicit-any */
export const getWidgetOptions: (
  chainId: number,
  address: string,
) => Omit<TradingView.ChartingLibraryWidgetOptions, 'datafeed'> = (chainId, address) => {
  // const poolList = queryClient.getQueryState<ISpotFullPool[]>(QUERY_KEYS.SPOT.POOLS(chainId));
  // const pool = poolList?.data?.find((p) => p.address == address);
  return {
    container: 'tv-chart',
    library_path: '/tradingView/charting_library/', // relative to public folder
    custom_css_url: '../themed.css',
    autosize: true,
    favorites: isMobile
      ? undefined
      : {
          intervals: ['1', '5', '15', '30', '60', '240', '1D', '1W'] as ResolutionString[],
        },
    disabled_features: [
      // 'use_localstorage_for_settings',
      'header_symbol_search',
      'header_compare',
      'symbol_search_hot_key',
      'save_chart_properties_to_local_storage',
      'timeframes_toolbar',
      'header_screenshot',
      'header_settings',
      'header_undo_redo',
      'header_quick_search',
    ],
    enabled_features: ['iframe_loading_compatibility_mode'],
    // @TODO(tradeview):change to symbol a head
    // symbol: `${pool?.symbol}::${address}`,
    symbol: `${address}`,

    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone as CustomTimezones,
    interval: defaultResolutionString as TradingView.ResolutionString,
    fullscreen: false,
    locale: 'en',
  };
};

const upColor = '#0FDB86';
const downColor = '#FF6666';
export const getWidgetOverrides = ({ theme }: { theme: TradingView.ThemeName }) => {
  return {
    theme,
    overrides: {
      ...TvTheme[theme],
      'scalesProperties.crosshairLabelBgColorDark': colors.dark.borderlightline1,
      'scalesProperties.crosshairLabelBgColorLight': colors.light.borderlightline1,
      // 'paneProperties.vertGridProperties.color': 'transparent',
      // 'paneProperties.horzGridProperties.color': 'transparent',
      'mainSeriesProperties.candleStyle.upColor': upColor,
      'mainSeriesProperties.candleStyle.downColor': downColor,
      'mainSeriesProperties.candleStyle.borderUpColor': upColor,
      'mainSeriesProperties.candleStyle.borderDownColor': downColor,
      'mainSeriesProperties.candleStyle.wickUpColor': upColor,
      'mainSeriesProperties.candleStyle.wickDownColor': downColor,
      'mainSeriesProperties.hollowCandleStyle.upColor': upColor,
      'mainSeriesProperties.hollowCandleStyle.downColor': downColor,
      'mainSeriesProperties.hollowCandleStyle.borderUpColor': upColor,
      'mainSeriesProperties.hollowCandleStyle.borderDownColor': downColor,
      'mainSeriesProperties.haStyle.upColor': upColor,
      'mainSeriesProperties.haStyle.downColor': downColor,
    } as TradingView.ChartPropertiesOverrides,
  };
};

export const klineIntervalToResolution = (interval: KlineInterval) => {
  switch (interval) {
    case KlineInterval.MINUTE:
      return '1';
    case KlineInterval.FIVE_MINUTE:
      return '5';
    case KlineInterval.FIFTEEN_MINUTE:
      return '15';
    case KlineInterval.THIRTY_MINUTE:
      return '30';
    case KlineInterval.HOUR:
      return '60';
    case KlineInterval.FOUR_HOUR:
      return '240';
    case KlineInterval.DAY:
      return '1D';
    case KlineInterval.WEEK:
      return '1W';
    case KlineInterval.MONTH:
      return '1M';
  }
  return '60';
};

export const resolutionToKlineInterval = (resolution: TradingView.ResolutionString) => {
  switch (resolution) {
    case '1':
      return KlineInterval.MINUTE;
    case '5':
      return KlineInterval.FIVE_MINUTE;
    case '15':
      return KlineInterval.FIFTEEN_MINUTE;
    case '30':
      return KlineInterval.THIRTY_MINUTE;
    case '60':
      return KlineInterval.HOUR;
    case '240':
      return KlineInterval.FOUR_HOUR;
    case '1D':
      return KlineInterval.DAY;
    case '1W':
      return KlineInterval.WEEK;
    case '1M':
      return KlineInterval.MONTH;
  }

  return KlineInterval.HOUR;
};
export const defaultSymbol = (symbol: string, address: string): any => {
  const supported_resolutions = Object.keys(TVResolution) as never;
  return {
    name: symbol.toLocaleUpperCase(),
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone as CustomTimezones,
    minmov: 1,
    // minmov2: 0,
    // pointvalue: 1,
    fractional: false,
    session: '24x7', // set cycle
    has_intraday: true,
    has_no_volume: false,
    has_empty_bars: true,
    has_daily: true, // Set whether to support week-month line
    has_weekly_and_monthly: true, // Set whether to support week-month line
    description: symbol.toLocaleUpperCase(),
    pricescale: 100000,
    volume_precision: 4,
    ticker: address.toLocaleUpperCase(),
    supported_resolutions: supported_resolutions,
  };
};

export const getSymbolInfo = (poolAddress: string, pool?: IMarketPair): LibrarySymbolInfo & { pool?: IMarketPair } => {
  const pairInfos: IMarketPair[] | undefined = queryClient.getQueryData(
    QUERY_KEYS.MARKET.PAIR_LIST(pool?.chainId || DEFAULT_CHAIN_ID),
  );
  if (!pool || poolAddress !== pool?.instrumentAddr) {
    const pairInfo = pairInfos?.find((p) => p.instrumentAddr === poolAddress);
    if (pairInfo) {
      pool = pairInfo;
    }
  }

  // if (pairInfo) {
  //   return {
  //     name: symbol,
  //     description: '',
  //   };
  // }

  const template = defaultSymbol(pool?.displaySymbol || '', poolAddress);
  return {
    ...template,
    pool,
  };
};

export const getPriceScale = (price: BigNumber.Value, max = 12) => {
  const bigNum = new BigNumber(price);
  if (bigNum.gte(10000)) return 0;
  if (bigNum.gte(10)) return 2;
  if (bigNum.gte(1e-4)) return 4;
  const { e } = bigNum;
  if (!e) return 4;
  const d = new BigNumber(1).plus(e).abs().toNumber() + 4;
  if (d > max) return 12;
  return d;
};

export function timesDecimals(a?: BigNumber.Value, decimals?: string | number) {
  if (!a) return new BigNumber(0);
  const bigA = BigNumber.isBigNumber(a) ? a : new BigNumber(a || '');
  if (bigA.isNaN()) return new BigNumber(0);
  if (typeof decimals === 'string' && decimals.length > 10) {
    return bigA.times(decimals);
  }
  return bigA.times(`1e${decimals ?? 18}`);
}
